import { useCallback, useEffect, useState, type FormEvent } from "react";
import AdminShell from "@/pages/admin/components/AdminShell";
import {
  clearAdminKey,
  fetchAlerts,
  fetchStats,
  getAdminKey,
  rebuildBaseline,
  resolveAlert,
  runIntegrityCheck,
  setAdminKey,
  triggerSpamScan,
} from "@/services/securityService";
import type {
  IntegrityResult,
  SecurityAlert,
  SecurityStats,
  SpamScanResult,
} from "@/services/securityService";

const severityStyles: Record<string, string> = {
  critical: "bg-primary-600 text-background-50",
  high: "bg-primary-500 text-background-50",
  medium: "bg-accent-500 text-background-50",
  low: "bg-secondary-500 text-background-50",
};

const statusStyles: Record<string, string> = {
  ok: "bg-accent-100 text-accent-900",
  changed: "bg-primary-100 text-primary-800",
  no_baseline: "bg-secondary-100 text-secondary-900",
};

const formatTime = (iso?: string | null): string => {
  if (!iso) return "—";
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
};

export default function SecurityDashboardPage() {
  const [keyInput, setKeyInput] = useState(getAdminKey());
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [stats, setStats] = useState<SecurityStats | null>(null);
  const [alerts, setAlerts] = useState<SecurityAlert[]>([]);
  const [integrity, setIntegrity] = useState<IntegrityResult | null>(null);
  const [scan, setScan] = useState<SpamScanResult | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [statsRes, alertsRes] = await Promise.all([fetchStats(), fetchAlerts()]);
      setStats(statsRes);
      setAlerts(alertsRes.alerts || []);
      setAuthed(true);
    } catch (err) {
      setAuthed(false);
      setError(err instanceof Error ? err.message : "Unable to load security data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (getAdminKey()) void load();
  }, [load]);

  const handleConnect = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setAdminKey(keyInput);
    void load();
  };

  const handleDisconnect = () => {
    clearAdminKey();
    setAuthed(false);
    setKeyInput("");
    setAlerts([]);
    setStats(null);
    setIntegrity(null);
    setScan(null);
    setNotice("");
    setError("");
  };

  const runAction = async (label: string, fn: () => Promise<void>) => {
    setBusy(label);
    setError("");
    setNotice("");
    try {
      await fn();
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : `${label} failed.`);
    } finally {
      setBusy("");
    }
  };

  const handleResolve = (id: string) =>
    runAction("Resolving alert", async () => {
      await resolveAlert(id);
      setNotice("Alert marked as resolved.");
    });

  const handleScan = () =>
    runAction("Running spam scan", async () => {
      const result = await triggerSpamScan();
      setScan(result);
      setNotice(`Scan complete — ${result.scanned} entries checked, ${result.flagged} flagged.`);
    });

  const handleIntegrity = () =>
    runAction("Running integrity check", async () => {
      const result = await runIntegrityCheck();
      setIntegrity(result);
      setNotice(
        result.status === "ok"
          ? "Integrity check passed — no unexpected file changes."
          : `Integrity check found ${result.changedFiles.length + result.addedFiles.length + result.removedFiles.length} change(s).`,
      );
    });

  const handleBaseline = () =>
    runAction("Rebuilding baseline", async () => {
      const result = await rebuildBaseline();
      setNotice(`Integrity baseline rebuilt — ${result.totalFiles} files hashed and stored.`);
    });

  const openAlerts = alerts.filter((a) => a.status !== "resolved");

  if (!authed) {
    return (
      <AdminShell
        title="Security Dashboard"
        subtitle="Monitor alerts, run spam scans and verify file integrity."
        active="security"
      >
        <div className="mx-auto max-w-lg rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
          <span className="flex h-12 w-12 items-center justify-center rounded-md bg-primary-100 text-primary-700">
            <i className="ri-lock-2-line text-2xl" aria-hidden="true" />
          </span>
          <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">Connect to your backend</h2>
          <p className="mt-2 text-sm leading-relaxed text-foreground-600">
            Enter the admin key you set as <code className="rounded bg-background-200 px-1.5 py-0.5 text-xs">SECURITY_ADMIN_KEY</code> on
            your PHP or Node server. It is sent as a request header and kept only in this browser tab.
          </p>
          <form onSubmit={handleConnect} className="mt-5 space-y-3">
            <input
              type="password"
              name="securityKey"
              autoComplete="off"
              value={keyInput}
              onChange={(event) => setKeyInput(event.target.value)}
              placeholder="Enter admin key"
              className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
            />
            <button
              type="submit"
              disabled={loading}
              className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
            >
              <i className="ri-plug-line" aria-hidden="true" />
              {loading ? "CONNECTING..." : "CONNECT"}
            </button>
          </form>
          {error && (
            <p role="status" className="mt-3 rounded-md bg-primary-100 px-3 py-2 text-xs text-primary-800">
              {error}
            </p>
          )}
          <p className="mt-4 text-xs leading-relaxed text-foreground-500">
            The endpoint is configured with <code className="rounded bg-background-200 px-1.5 py-0.5">VITE_PUBLIC_SECURITY_ENDPOINT</code>{" "}
            (default <code className="rounded bg-background-200 px-1.5 py-0.5">/security.php</code>).
          </p>
        </div>
      </AdminShell>
    );
  }

  return (
    <AdminShell
      title="Security Dashboard"
      subtitle="Monitor alerts, run spam scans and verify file integrity."
      active="security"
    >
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => void load()}
          disabled={loading}
          className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700 disabled:opacity-60"
        >
          <i className={`ri-refresh-line ${loading ? "animate-spin" : ""}`} aria-hidden="true" /> Refresh
        </button>
        <button
          type="button"
          onClick={handleScan}
          disabled={busy !== ""}
          className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60"
        >
          <i className="ri-radar-line" aria-hidden="true" /> Run spam scan
        </button>
        <button
          type="button"
          onClick={handleIntegrity}
          disabled={busy !== ""}
          className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600 disabled:opacity-60"
        >
          <i className="ri-fingerprint-line" aria-hidden="true" /> Run integrity check
        </button>
        <button
          type="button"
          onClick={handleBaseline}
          disabled={busy !== ""}
          className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700 disabled:opacity-60"
        >
          <i className="ri-archive-drawer-line" aria-hidden="true" /> Rebuild baseline
        </button>
        <button
          type="button"
          onClick={handleDisconnect}
          className="ml-auto inline-flex items-center gap-2 whitespace-nowrap rounded-md px-4 py-2 text-sm font-semibold text-foreground-600 transition-colors hover:text-primary-700"
        >
          <i className="ri-logout-box-r-line" aria-hidden="true" /> Disconnect
        </button>
      </div>

      {busy && (
        <p role="status" className="mt-4 rounded-md bg-background-50 px-3 py-2 text-xs text-foreground-600">
          <i className="ri-loader-4-line mr-1.5 inline-block animate-spin" aria-hidden="true" /> {busy}…
        </p>
      )}
      {notice && !busy && (
        <p role="status" className="mt-4 rounded-md bg-accent-100 px-3 py-2 text-xs text-accent-900">
          {notice}
        </p>
      )}
      {error && (
        <p role="status" className="mt-4 rounded-md bg-primary-100 px-3 py-2 text-xs text-primary-800">
          {error}
        </p>
      )}

      {/* Stats */}
      <section className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon="ri-alert-line" label="Open alerts" value={stats?.open ?? openAlerts.length} tone="primary" />
        <StatCard icon="ri-shield-check-line" label="Resolved" value={stats?.resolved ?? 0} tone="accent" />
        <StatCard icon="ri-stack-line" label="Total alerts" value={stats?.total ?? alerts.length} tone="secondary" />
        <StatCard
          icon="ri-fingerprint-line"
          label="Integrity"
          value={stats?.integrity?.status === "ok" ? "OK" : stats?.integrity?.status || "—"}
          tone="foreground"
        />
      </section>

      {/* Integrity + scan results */}
      <section className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-lg border border-background-200 bg-background-50 p-5">
          <h3 className="font-heading text-base font-semibold text-foreground-950">Last integrity check</h3>
          {integrity ? (
            <div className="mt-3 space-y-2 text-sm text-foreground-700">
              <p className="flex flex-wrap items-center gap-2">
                Status:
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${statusStyles[integrity.status] || "bg-background-200 text-foreground-800"}`}>
                  {integrity.status}
                </span>
                <span className="text-xs text-foreground-500">· {formatTime(integrity.checkedAt)}</span>
              </p>
              <p className="text-xs text-foreground-600">
                {integrity.totalFiles} files hashed · {integrity.changedFiles.length} changed · {integrity.addedFiles.length} added ·{" "}
                {integrity.removedFiles.length} removed
              </p>
              {[...integrity.changedFiles, ...integrity.addedFiles, ...integrity.removedFiles].slice(0, 8).map((file) => (
                <p key={file} className="truncate rounded bg-background-100 px-2 py-1 font-mono text-xs text-foreground-700">
                  {file}
                </p>
              ))}
            </div>
          ) : (
            <p className="mt-3 text-sm text-foreground-600">
              Run a check to compare your deployed files against the stored baseline.
            </p>
          )}
        </div>

        <div className="rounded-lg border border-background-200 bg-background-50 p-5">
          <h3 className="font-heading text-base font-semibold text-foreground-950">Last spam scan</h3>
          {scan ? (
            <div className="mt-3 space-y-2 text-sm text-foreground-700">
              <p className="text-xs text-foreground-600">
                {scan.scanned} entries scanned · {scan.flagged} flagged · {formatTime(scan.checkedAt)}
              </p>
              {scan.findings.length === 0 ? (
                <p className="rounded bg-accent-100 px-2 py-1 text-xs text-accent-900">No spam patterns found.</p>
              ) : (
                scan.findings.slice(0, 8).map((finding) => (
                  <p key={finding.id} className="rounded bg-primary-100 px-2 py-1 text-xs text-primary-800">
                    {finding.id}: {finding.reasons.join("; ")}
                  </p>
                ))
              )}
            </div>
          ) : (
            <p className="mt-3 text-sm text-foreground-600">
              Scan new form submissions for spam patterns like link stuffing, spam keywords and repeated characters.
            </p>
          )}
        </div>
      </section>

      {/* Alerts */}
      <section className="mt-6 rounded-lg border border-background-200 bg-background-50">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-background-200 px-5 py-4">
          <h3 className="font-heading text-base font-semibold text-foreground-950">Alerts</h3>
          <span className="text-xs text-foreground-500">{openAlerts.length} open</span>
        </div>
        {alerts.length === 0 ? (
          <p className="px-5 py-8 text-center text-sm text-foreground-600">No alerts yet. That is a good sign.</p>
        ) : (
          <ul className="divide-y divide-background-200">
            {alerts.map((alert) => (
              <li key={alert.id} className="flex flex-col gap-3 px-5 py-4 md:flex-row md:items-center md:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                        severityStyles[alert.severity] || "bg-secondary-500 text-background-50"
                      }`}
                    >
                      {alert.severity}
                    </span>
                    <span className="text-xs font-semibold uppercase tracking-wide text-foreground-500">{alert.type}</span>
                    {alert.status === "resolved" && (
                      <span className="rounded-full bg-background-200 px-2.5 py-0.5 text-xs font-semibold text-foreground-600">
                        resolved
                      </span>
                    )}
                  </div>
                  <p className="mt-1.5 text-sm text-foreground-800">{alert.message}</p>
                  <p className="mt-0.5 text-xs text-foreground-500">{formatTime(alert.createdAt)}</p>
                </div>
                {alert.status !== "resolved" && (
                  <button
                    type="button"
                    onClick={() => handleResolve(alert.id)}
                    disabled={busy !== ""}
                    className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-3 py-2 text-xs font-semibold text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700 disabled:opacity-60"
                  >
                    <i className="ri-check-line" aria-hidden="true" /> Resolve
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </AdminShell>
  );
}

interface StatCardProps {
  icon: string;
  label: string;
  value: number | string;
  tone: "primary" | "accent" | "secondary" | "foreground";
}

const toneStyles: Record<StatCardProps["tone"], string> = {
  primary: "bg-primary-100 text-primary-700",
  accent: "bg-accent-100 text-accent-800",
  secondary: "bg-secondary-100 text-secondary-800",
  foreground: "bg-background-200 text-foreground-700",
};

function StatCard({ icon, label, value, tone }: StatCardProps) {
  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-5">
      <span className={`flex h-10 w-10 items-center justify-center rounded-md ${toneStyles[tone]}`}>
        <i className={`${icon} text-xl`} aria-hidden="true" />
      </span>
      <p className="mt-3 font-heading text-2xl font-bold text-foreground-950">{value}</p>
      <p className="text-xs text-foreground-500">{label}</p>
    </div>
  );
}